import React, { useState, useMemo, useEffect, useCallback, useRef, Component } from 'react';
import YooptaEditor, { createYooptaEditor } from '@yoopta/editor';
import Paragraph from '@yoopta/paragraph';
import Blockquote from '@yoopta/blockquote';
import Headings from '@yoopta/headings';
import Lists from '@yoopta/lists';
import Link from '@yoopta/link';
import Image from '@yoopta/image';
import Table from '@yoopta/table';
import Divider from '@yoopta/divider';
import Accordion from '@yoopta/accordion';
import Code from '@yoopta/code';
import Embed from '@yoopta/embed';
import File from '@yoopta/file';
import Callout from '@yoopta/callout';
import Video from '@yoopta/video';
import { Bold, Italic, CodeMark, Underline, Strike, Highlight } from '@yoopta/marks';
import Toolbar, { DefaultToolbarRender } from '@yoopta/toolbar';
import ActionMenu, { DefaultActionMenuRender } from '@yoopta/action-menu-list';
import LinkTool, { DefaultLinkToolRender } from '@yoopta/link-tool';
import { html } from '@yoopta/exports';
import { useTranslation } from '../../redux/TranslationHelper';
import HtmlCssModal from './HtmlCssModal';
import PureHtmlEditorModal from './PureHtmlEditorModal';
import { generateScopeId, applyScopedCSS, removeScopedCSS, sanitizeCSS } from '../../utils/cssScoper';
import './YooptaEditor.css';

// 커스텀 툴바 렌더링 함수
const CustomToolbarRender = (props) => {
  const { editor, editorKey = 'default', onPureHtmlSave, getCurrentHtmlContent } = props;
  const [showHtmlCssModal, setShowHtmlCssModal] = useState(false);
  const [showPureHtmlModal, setShowPureHtmlModal] = useState(false);
  
  // 에디터가 없으면 기본 툴바 렌더링
  if (!editor) {
    return DefaultToolbarRender(props);
  }
  
  // 에디터 포커스를 안전하게 제거하는 헬퍼 함수
  const clearEditorFocus = () => {
    try {
      // DOM이 존재하는지 먼저 확인
      const editorElement = document.querySelector(`[data-yoopta-editor-id="${editorKey}"]`);
      if (!editorElement) {
        console.warn('[YooptaEditor] 에디터 DOM 요소를 찾을 수 없음');
        return;
      }

      // Slate의 ReactEditor를 통한 안전한 deselect
      if (typeof editor.deselect === 'function') {
        editor.deselect();
      }

      // selection 제거는 DOM 조작 후에 실행
      setTimeout(() => {
        try {
          if (editor.selection) {
            Object.assign(editor, { selection: null });
          }
        } catch (selectionError) {
          console.warn('[YooptaEditor] selection 제거 실패 (무시됨):', selectionError);
        }
      }, 10);

    } catch (error) {
      console.warn('[YooptaEditor] 포커스 제거 실패 (무시됨):', error);
    }
  };
  
  const handleHeadingChange = (type) => {
    try {
      // 에디터 포커스 제거 - 변경 전
      clearEditorFocus();
      
      // Yoopta의 올바른 API 사용
      if (typeof editor.insertBlock === 'function') {
        // 새 블록 삽입
        editor.insertBlock(type);
      } else if (typeof editor.toggleBlock === 'function') {
        // 블록 타입 토글
        editor.toggleBlock(type);
      } else {
        console.warn('사용 가능한 블록 API를 찾을 수 없습니다');
      }
      
      // focus 제거 - Slate DOM 오류 방지
    } catch (error) {
      console.error('블록 타입 변경 실패:', error);
    }
  };

  const applyMark = (mark) => {
    try {
      // 에디터 포커스 제거 - 변경 전
      clearEditorFocus();
      
      // Yoopta Editor의 올바른 mark API 사용
      if (editor.formats && editor.formats[mark]) {
        // formats API 사용
        editor.formats[mark].toggle();
      } else if (editor.marks && editor.marks[mark]) {
        // marks API 사용
        editor.marks[mark].toggle();
      } else if (typeof editor.toggleMark === 'function') {
        // toggleMark API 사용
        editor.toggleMark(mark);

      }
      
      // focus 제거 - Slate DOM 오류 방지
    } catch (error) {
      console.error('마크 적용 실패:', error);
    }
  };

  // HTML/CSS 코드 적용 핸들러
  const handleHtmlCssApply = (combinedCode) => {
    try {

      // 1. 먼저 style 태그를 분리하여 추출
      let styles = '';
      let htmlOnly = combinedCode;
      
      // style 태그 추출 (대소문자 구분 없이)
      const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
      const styleMatches = combinedCode.match(styleRegex);
      
      if (styleMatches) {
        // 모든 style 태그의 내용을 추출
        styles = styleMatches.map(match => {
          const content = match.replace(/<\/?style[^>]*>/gi, '');
          return content.trim();
        }).filter(s => s).join('\n\n');
        
        // HTML에서 style 태그 제거
        htmlOnly = combinedCode.replace(styleRegex, '');
        
      }
      
      // 2. style 태그가 있으면 처리
      let sanitizedCSS = '';
      if (styles) {
        // 에디터별 스코프 ID 가져오기 또는 생성
        let scopeId = editorScopes.get(editorKey);
        if (!scopeId) {
          scopeId = generateScopeId('yoopta');
          editorScopes.set(editorKey, scopeId);
        }
        
        // CSS 정제 (보안)
        sanitizedCSS = sanitizeCSS(styles);
        
        // 전역 스타일 저장소에 원본 CSS 저장 (스코프 없이)
        globalStyles.set(editorKey, sanitizedCSS);

        // 에디터 표시용으로만 스코프가 적용된 CSS를 head에 추가
        applyScopedCSS(sanitizedCSS, scopeId, `custom-style-${editorKey}`, {
          preserveRootVars: true,  // :root의 CSS 변수 보존
          scopeKeyframes: true     // keyframes 이름에 스코프 추가
        });
        
      }
      
      // 3. HTML을 Yoopta 블록 형식으로 변환
      const htmlWrapper = `<div>${htmlOnly}</div>`;
      let parsedBlocks = {};
      
      try {
        // 에디터 상태 초기화
        clearEditorFocus();
        
        // html.deserialize를 사용하여 HTML을 Yoopta 형식으로 파싱
        parsedBlocks = html.deserialize(editor, htmlWrapper);
      } catch (parseError) {
        console.warn('[YooptaEditor] HTML 파싱 실패, 대체 방법 사용:', parseError);
        
        // 파싱 실패 시 Callout 블록으로 HTML 코드 표시
        const blockId = `callout-${Date.now()}`;
        const elementId = `element-${Date.now()}`;
        parsedBlocks = {
          [blockId]: {
            id: blockId,
            type: 'Callout',
            value: [
              {
                id: elementId,
                type: 'callout',
                children: [{ 
                  text: styles ? 
                    `HTML이 적용되었습니다. CSS 스타일이 문서에 추가되었습니다.\n\nHTML:\n${htmlOnly}` : 
                    `HTML이 적용되었습니다:\n\n${htmlOnly}`
                }],
                props: {
                  theme: 'info'
                }
              }
            ],
            meta: {
              order: 0,
              depth: 0,
            },
          }
        };
      }
      
      // 기존 블록들을 새 블록들로 완전 대체
      const newValue = parsedBlocks;
      

      // 에디터 포커스 제거 - DOM 동기화 문제 방지
      clearEditorFocus();
      
      // DOM 업데이트를 위한 지연 처리
      setTimeout(() => {
        try {
          // 1. 에디터에 직접 값 설정
          if (typeof editor.setEditorValue === 'function') {
            editor.setEditorValue(newValue);
          }
          
          // 2. 전역 상태 업데이트 호출 (외부에서 설정)
          if (window.currentYooptaValueSetter) {
            window.currentYooptaValueSetter(newValue);
          }
          
          // 3. onChange 콜백 호출
          if (window.currentYooptaOnChange) {
            let finalHtml = html.serialize(editor, newValue);
            
            // onChange에 전달할 때 원본 CSS를 style 태그로 포함
            if (sanitizedCSS) {
              finalHtml = `<style>\n${sanitizedCSS}\n</style>\n\n${finalHtml}`;
            }
            
            window.currentYooptaOnChange(finalHtml, newValue);
          }
        } catch (error) {
          console.error('[YooptaEditor] 에디터 값 업데이트 실패:', error);
          // 오류 발생 시 에디터 재마운트 시도
          if (window.requestYooptaRemount) {
            window.requestYooptaRemount();
          }
        }
      }, 100);
      
      setShowHtmlCssModal(false);
      

    } catch (error) {
      console.error('HTML/CSS 적용 실패:', error);
      
      // 최종 대안: 직접 블록 생성
      try {
        const blockId = `paragraph-${Date.now()}`;
        
        const elementId = `element-${Date.now()}`;
        const newBlock = {
          id: blockId,
          type: 'Paragraph',
          value: [
            {
              id: elementId,
              type: 'paragraph', 
              children: [{ text: '⚠️ HTML/CSS 코드를 적용할 수 없습니다. 다음 코드를 확인해주세요:\n\n' + combinedCode }]
            }
          ],
          meta: {
            order: 0,
            depth: 0,
          },
        };
        
        const newValue = {
          [blockId]: newBlock
        };
        
        // 에디터 포커스 제거
        clearEditorFocus();
        
        // DOM 업데이트를 위한 지연 처리
        setTimeout(() => {
          try {
            // 에디터에 직접 값 설정
            if (typeof editor.setEditorValue === 'function') {
              editor.setEditorValue(newValue);
            }
            
            // 전역 상태 업데이터 호출
            if (window.currentYooptaValueSetter) {
              window.currentYooptaValueSetter(newValue);
            }
            
            // onChange 콜백 직접 호출
            if (window.currentYooptaOnChange) {
              const htmlContent = html.serialize(editor, newValue);
              window.currentYooptaOnChange(htmlContent, newValue);
            }
          } catch (error) {
            console.warn('[YooptaEditor] 최종 대안 에디터 설정 실패:', error);
          }
        }, 100);
        setShowHtmlCssModal(false);
        
      } catch (finalError) {
        console.error('최종 대안도 실패:', finalError);
        alert('HTML/CSS 적용에 실패했습니다. 코드를 확인해주세요.');
      }
    }
  };

  // Pure HTML 에디터 저장 핸들러
  const handlePureHtmlSave = (htmlContent, blocks) => {
    // blocks는 null이므로 HTML만 저장됨
    if (onPureHtmlSave) {
      onPureHtmlSave(htmlContent, null);
    } else {
      // onPureHtmlSave가 없으면 onChange 호출
      if (window.currentYooptaOnChange) {
        window.currentYooptaOnChange(htmlContent, null);
      }
    }
    setShowPureHtmlModal(false);
  };
  
  return (
    <div className="custom-yoopta-toolbar" style={{
      backgroundColor: '#f8f9fa',
      minHeight: '60px',
      display: 'flex',
      alignItems: 'center'
    }}>
      {/* 제목 스타일 드롭다운 */}
      <div className="toolbar-group">
        <select 
          className="toolbar-dropdown"
          onChange={(e) => handleHeadingChange(e.target.value)}
          defaultValue="Paragraph"
        >
          <option value="Paragraph">일반 텍스트</option>
          <option value="HeadingOne">제목 1</option>
          <option value="HeadingTwo">제목 2</option>
          <option value="HeadingThree">제목 3</option>
          <option value="Blockquote">인용구</option>
          <option value="Code">코드 블록</option>
          <option value="Callout">콜아웃</option>
        </select>
      </div>

      <div className="toolbar-divider"></div>

      {/* 텍스트 포맷팅 버튼들 */}
      <div className="toolbar-group">
        <button 
          className="toolbar-button"
          onClick={() => applyMark('bold')}
          title="굵게 (Ctrl+B)"
        >
          <strong>B</strong>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => applyMark('italic')}
          title="기울임 (Ctrl+I)"
        >
          <em>I</em>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => applyMark('underline')}
          title="밑줄 (Ctrl+U)"
        >
          <u>U</u>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => applyMark('strike')}
          title="취소선"
        >
          <s>S</s>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => applyMark('code')}
          title="코드"
        >
          <span className="code-icon">&lt;/&gt;</span>
        </button>
      </div>

      {/* <div className="toolbar-divider"></div> */}

      {/* 코드 및 하이라이트 */}
      {/* <div className="toolbar-group">
       
        <button 
          className="toolbar-button"
          onClick={() => applyMark('highlight')}
          title="형광펜"
        >
          <span className="highlight-icon">🖍</span>
        </button>
      </div> */}

      <div className="toolbar-divider"></div>

      {/* 리스트 버튼들 */}
      <div className="toolbar-group">
        <button 
          className="toolbar-button"
          onClick={() => {
            clearEditorFocus();
            handleHeadingChange('BulletedList');
          }}
          title="글머리 기호 목록"
        >
          <span className="list-icon">•</span>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => {
            clearEditorFocus();
            handleHeadingChange('NumberedList');
          }}
          title="번호 매기기 목록"
        >
          <span className="list-icon">1.</span>
        </button>
        {/* <button 
          className="toolbar-button"
          onClick={() => handleHeadingChange('TodoList')}
          title="할 일 목록"
        >
          <span className="list-icon">☑</span>
        </button> */}
      </div>

      <div className="toolbar-divider"></div>

      {/* 링크 및 이미지 */}
      <div className="toolbar-group">
        <button 
          className="toolbar-button"
          onClick={() => {
            try {
              // 에디터 포커스 제거
              clearEditorFocus();
              
              // 링크 삽입
              const url = prompt('링크 URL을 입력하세요:');
              if (url) {
                // Link 블록으로 삽입
                editor.insertBlock('Link', {
                  url: url,
                  text: url
                });
              }
            } catch (error) {
              console.error('링크 추가 실패:', error);
              try {
                editor.insertBlock('Link');
              } catch (fallbackError) {
                console.error('대안도 실패:', fallbackError);
              }
            }
          }}
          title="링크 추가"
        >
          <span className="link-icon">🔗</span>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => {
            try {
              // 에디터 포커스 제거
              clearEditorFocus();
              
              // 🔢 현재 에디터의 블록 순서 상태 확인
              const currentValue = editor.getEditorValue() || {};
              const currentBlocks = Object.values(currentValue);
              const maxOrder = currentBlocks.length > 0 ? Math.max(...currentBlocks.map(block => block.meta?.order || 0)) : -1;
              

              // Yoopta의 기본 이미지 블록 삽입 (빈 이미지 블록 생성)
              editor.insertBlock('Image');

              // 삽입 후 상태 확인
              setTimeout(() => {
                const newValue = editor.getEditorValue() || {};
                const newBlocks = Object.values(newValue);
              }, 100);
            } catch (error) {
              console.error('이미지 블록 추가 실패:', error);
            }
          }}
          title="이미지 추가"
        >
          <span className="image-icon">🖼</span>
        </button>
      </div>

      <div className="toolbar-divider"></div>

      {/* 기타 기능들 */}
      <div className="toolbar-group">
        <button 
          className="toolbar-button"
          onClick={() => {
            try {
              clearEditorFocus();
              editor.insertBlock('Table');
            } catch (error) {
              console.error('표 추가 실패:', error);
            }
          }}
          title="표 추가"
        >
          <span className="table-icon">⊞</span>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => {
            try {
              clearEditorFocus();
              editor.insertBlock('Divider');
            } catch (error) {
              console.error('구분선 추가 실패:', error);
            }
          }}
          title="구분선"
        >
          <span className="divider-icon">—</span>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => {
            try {
              clearEditorFocus();
              editor.insertBlock('Accordion');
            } catch (error) {
              console.error('아코디언 추가 실패:', error);
            }
          }}
          title="아코디언"
        >
          <span className="accordion-icon">▼</span>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => {
            try {
              clearEditorFocus();
              editor.insertBlock('Embed');
            } catch (error) {
              console.error('임베드 추가 실패:', error);
            }
          }}
          title="임베드"
        >
          <span className="embed-icon">📺</span>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => {
            try {
              // 에디터 포커스 제거
              clearEditorFocus();
              
              // 🔢 현재 에디터의 블록 순서 상태 확인
              const currentValue = editor.getEditorValue() || {};
              const currentBlocks = Object.values(currentValue);
              const maxOrder = currentBlocks.length > 0 ? Math.max(...currentBlocks.map(block => block.meta?.order || 0)) : -1;
              

              // Yoopta의 기본 파일 블록 삽입 (빈 파일 블록 생성)
              editor.insertBlock('File');

              // 삽입 후 상태 확인
              setTimeout(() => {
                const newValue = editor.getEditorValue() || {};
                const newBlocks = Object.values(newValue);
              }, 100);
            } catch (error) {
              console.error('파일 블록 추가 실패:', error);
            }
          }}
          title="파일"
        >
          <span className="file-icon">📁</span>
        </button>
        {/* <button 
          className="toolbar-button"
          onClick={() => {
            try {
              // 비디오 블록 직접 삽입
              editor.insertBlock('Video');
            } catch (error) {
              console.error('비디오 추가 실패:', error);
            }
          }}
          title="비디오"
        >
          <span className="video-icon">🎬</span>
        </button> */}
      </div>

      <div className="toolbar-divider"></div>

      {/* HTML/CSS 코드 편집 */}
      <div className="toolbar-group">
        <button 
          className="toolbar-button"
          onClick={() => setShowHtmlCssModal(true)}
          title="HTML/CSS 코드 편집"
        >
          <span className="html-icon" style={{ fontSize: '20px' }}>🌐</span>
        </button>
        <button 
          className="toolbar-button"
          onClick={() => setShowPureHtmlModal(true)}
          title="Pure HTML 편집"
          style={{ marginLeft: '5px' }}
        >
          <span className="html-icon" style={{ fontSize: '16px' }}>HTML</span>
        </button>
      </div>

      {/* HTML/CSS 모달 */}
      <HtmlCssModal
        isOpen={showHtmlCssModal}
        onClose={() => setShowHtmlCssModal(false)}
        onApply={handleHtmlCssApply}
        initialCode={(() => {
          try {
            // 현재 에디터의 내용을 HTML로 변환
            const currentValue = editor.getEditorValue();
            if (currentValue && Object.keys(currentValue).length > 0) {
              let htmlContent = html.serialize(editor, currentValue) || '';
              
              // 저장된 스타일이 있으면 추가 (원본 CSS로)
              const savedStyles = globalStyles.get(editorKey);
              if (savedStyles) {
                htmlContent = `<style>\n${savedStyles}\n</style>\n\n${htmlContent}`;
              }
              
              return htmlContent;
            }
          } catch (e) {
            console.warn('HTML 변환 실패:', e);
          }
          return '';
        })()}
      />
      
      {/* Pure HTML 모달 */}
      <PureHtmlEditorModal
        isOpen={showPureHtmlModal}
        onClose={() => setShowPureHtmlModal(false)}
        initialContent={(() => {
          try {
            // getCurrentHtmlContent 함수가 있으면 사용
            if (getCurrentHtmlContent && typeof getCurrentHtmlContent === 'function') {
              const content = getCurrentHtmlContent();
              return content;
            }
            
            // 폴백: 현재 에디터의 내용을 HTML로 변환
            const currentValue = editor.getEditorValue();
            if (currentValue && Object.keys(currentValue).length > 0) {
              let htmlContent = html.serialize(editor, currentValue) || '';
              
              // 저장된 스타일이 있으면 추가
              const savedStyles = globalStyles.get(editorKey);
              if (savedStyles) {
                htmlContent = `<style>\n${savedStyles}\n</style>\n\n${htmlContent}`;
              }
              
              return htmlContent;
            }
          } catch (e) {
            console.warn('HTML 변환 실패:', e);
          }
          return '';
        })()}
        onSave={handlePureHtmlSave}
      />
    </div>
  );
};

// 전역 에디터 상태 저장소 (탭 전환 시에도 유지)
const editorStateCache = new Map();

// 전역 스타일 저장소 - 스코프 ID와 함께 관리
const globalStyles = new Map();

// 전역 스코프 ID 저장소
const editorScopes = new Map();

// Error Boundary 컴포넌트 - Slate 오류 처리 강화
class YooptaErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    console.error('YooptaEditor Error Boundary 캐치:', error);
    
    // Slate DOM 오류인지 확인
    const isSlateError = error.message && (
      error.message.includes('Cannot resolve a DOM node from Slate node') ||
      error.message.includes('Cannot find a descendant') ||
      error.message.includes('Cannot get the leaf node')
    );
    
    if (isSlateError) {
      // 부모 컴포넌트에 재마운트 요청
      if (window.requestYooptaRemount) {
        setTimeout(() => window.requestYooptaRemount(), 100);
      }
    }
    
    return { hasError: true, error, isSlateError };
  }

  componentDidCatch(error, errorInfo) {
    console.error('YooptaEditor 오류:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Slate 오류인 경우 짧은 로딩 표시 후 자동 복구
      if (this.state.isSlateError) {
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '200px',
            color: '#6b7280'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '10px', fontSize: '20px' }}>🔄</div>
              <div>에디터를 복구하는 중...</div>
            </div>
          </div>
        );
      }
      
      return <div>에디터 로드 중 오류가 발생했습니다.</div>;
    }

    return this.props.children;
  }
}

const YooptaHtmlEditor = ({ initialData, onChange, placeholder = '여기에 내용을 입력하세요...', editorKey = 'default', autoFocus = false, onPureHtmlSave = null }) => {
  const { t } = useTranslation();
  
  // 에디터 재마운트를 위한 키 상태
  const [editorMountKey, setEditorMountKey] = useState(0);
  
  // 현재 HTML 콘텐츠를 저장하는 ref
  const currentHtmlRef = useRef('');
  
  // 에러 복구 상태
  const [isRecovering, setIsRecovering] = useState(false);
  
  // 스코프 ID 상태 - 컴포넌트별로 고유한 ID 유지
  const [scopeId] = useState(() => {
    // 기존 스코프 ID가 있으면 사용, 없으면 새로 생성
    let existingScopeId = editorScopes.get(editorKey);
    if (!existingScopeId) {
      existingScopeId = generateScopeId('yoopta');
      editorScopes.set(editorKey, existingScopeId);
    }
    return existingScopeId;
  });
  
  // editor 인스턴스를 editorKey와 mountKey로 안정화
  const editor = useMemo(() => {
    const newEditor = createYooptaEditor();
    
    // 새 에디터의 selection을 완전히 제거하고 안전 모드 설정
    if (newEditor) {
      try {
        // selection 완전 제거
        if (typeof newEditor.deselect === 'function') {
          newEditor.deselect();
        }
        
        // Slate 에디터 내부 selection 초기화
        Object.assign(newEditor, { 
          selection: null,
          operations: [],
          marks: null
        });
        
        // 포커스 관련 메서드 오버라이드하여 안전하게 처리
        const originalFocus = newEditor.focus;
        newEditor.focus = function() {
          try {
            // DOM이 준비되지 않았으면 포커스 시도하지 않음
            const editorElement = document.querySelector(`[data-yoopta-editor-id="${editorKey}"]`);
            if (!editorElement) {
              console.warn('[YooptaEditor] DOM이 준비되지 않아 포커스 건너뜀');
              return;
            }

            // DOM 노드가 Slate와 동기화되어 있는지 확인
            const slateNodes = document.querySelectorAll('[data-slate-node="element"]');
            if (slateNodes.length === 0) {
              console.warn('[YooptaEditor] Slate DOM 노드가 준비되지 않아 포커스 건너뜀');
              return;
            }
            
            // 원래 focus 메서드를 setTimeout으로 지연 호출하여 DOM 동기화 보장
            setTimeout(() => {
              try {
                if (originalFocus) {
                  return originalFocus.call(this);
                }
              } catch (focusError) {
                console.warn('[YooptaEditor] 지연된 포커스도 실패 (무시됨):', focusError);
              }
            }, 50);
            
          } catch (error) {
            console.warn('[YooptaEditor] 포커스 실패 (무시됨):', error);
          }
        };
        
      } catch (error) {
        console.warn('[YooptaEditor] 초기 에디터 설정 실패 (무시됨):', error);
      }
    }
    
    return newEditor;
  }, [editorKey, editorMountKey]);
  
  // 에디터 재마운트 함수를 전역으로 설정
  useEffect(() => {
    window.requestYooptaRemount = () => {
      setIsRecovering(true);
      
      // 짧은 지연 후 재마운트
      setTimeout(() => {
        setEditorMountKey(prev => prev + 1);
        setIsRecovering(false);
      }, 200);
    };
    
    return () => {
      if (window.requestYooptaRemount) {
        window.requestYooptaRemount = null;
      }
    };
  }, []);
  
  const [isReady, setIsReady] = useState(false);
  const [isDOMReady, setIsDOMReady] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const editorContainerRef = useRef(null);
  const selectionRootRef = useRef(null);
  const mountedRef = useRef(false);
  
  // initialData 변경 감지를 더 안정적으로 처리
  const initialDataRef = useRef(initialData);
  const hasInitialDataChanged = useRef(false);
  const lastProcessedDataRef = useRef(null);
  
  useEffect(() => {
    // onChange로 인한 순환 참조 방지
    if (!initialData) return;
    
    // 현재 처리된 데이터와 비교하여 실제 변경인지 확인
    const currentDataStr = JSON.stringify(initialData);
    const lastProcessedDataStr = JSON.stringify(lastProcessedDataRef.current);
    const prevDataStr = JSON.stringify(initialDataRef.current);
    
    // 이전 처리된 데이터와 동일하면 무시 (onChange로 인한 순환 방지)
    if (currentDataStr === lastProcessedDataStr) {
      return;
    }
    
    // 외부에서 실제로 새로운 데이터가 전달된 경우에만 처리
    if (currentDataStr !== prevDataStr && initialData) {
      hasInitialDataChanged.current = true;
      initialDataRef.current = initialData;
      lastProcessedDataRef.current = initialData;
      setIsInitialized(false);
      
      // 에디터 재마운트는 외부 데이터 변경시에만
      setEditorMountKey(prev => prev + 1);
      setIsReady(false);
      setIsDOMReady(false);
    }
  }, [initialData]);
  
  // 컴포넌트 마운트/언마운트 추적
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      // 전역 변수 정리
      if (window.currentYooptaOnChange) {
        window.currentYooptaOnChange = null;
      }
      if (window.currentYooptaValueSetter) {
        window.currentYooptaValueSetter = null;
      }
      if (window.requestYooptaRemount) {
        window.requestYooptaRemount = null;
      }
    };
  }, [editorKey]);
  
  // DOM 준비 상태 체크
  useEffect(() => {
    if (!mountedRef.current) return;
    
    const checkDOMReady = () => {
      // DOM 체크를 단순화 - 컨테이너만 존재하면 준비된 것으로 판단
      const containerExists = editorContainerRef.current !== null;
      const documentReady = document.readyState !== 'loading';
      
      const domReady = containerExists && documentReady;
      
      if (domReady && !isDOMReady) {
        setIsDOMReady(true);
      }
      
      return domReady;
    };
    
    // 즉시 체크
    if (checkDOMReady()) {
      return;
    }
    
    // DOM이 준비되지 않았으면 간격을 두고 체크
    const interval = setInterval(() => {
      if (checkDOMReady() || !mountedRef.current) {
        clearInterval(interval);
      }
    }, 50);
    
    // 최대 1초 후에는 강제로 준비 상태로 설정 (시간 단축)
    const timeout = setTimeout(() => {
      if (mountedRef.current && !isDOMReady) {
        setIsDOMReady(true);
      }
      clearInterval(interval);
    }, 1000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isDOMReady, editorMountKey]); // editorMountKey 변경 시에도 DOM 체크 재시작
  
  // 이미지 업로드 핸들러 (먼저 선언)
  const handleImageUpload = useCallback(async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'withcookie-bucket');
      formData.append('path', 'c/default');

      const response = await fetch('https://refrigerator.logipasta.com/v1/file', {
        method: 'POST',
        body: formData,
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
      });

      if (!response.ok) {
        throw new Error('이미지 업로드에 실패했습니다.');
      }

      const data = await response.json();
      return {
        src: data.file,
        alt: file.name,
      };
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      throw error;
    }
  }, []);

  // 전체 플러그인들 설정 (모든 기능 복구)
  const plugins = useMemo(() => [
    Paragraph,
    Blockquote,
    Headings.HeadingOne,
    Headings.HeadingTwo,
    Headings.HeadingThree,
    Lists.BulletedList,
    Lists.NumberedList,
    Lists.TodoList,
    Link,
    Image.extend({
      options: {
        async onUpload(file) {
          return await handleImageUpload(file);
        },
      },
    }),
    Table,
    Divider,
    Accordion,
    Code,
    Embed,
    File.extend({
      options: {
        async onUpload(file) {
          return await handleImageUpload(file);
        },
      },
    }),
    Callout,
    Video.extend({
      options: {
        async onUpload(file) {
          return await handleImageUpload(file);
        },
      },
    }),
  ], [handleImageUpload]);

  // 마크 설정
  const marks = useMemo(() => [Bold, Italic, CodeMark, Underline, Strike, Highlight], []);

  // 툴바와 액션 메뉴 설정 (커스텀 툴바 적용)
  const tools = useMemo(() => ({
    Toolbar: {
      tool: Toolbar,
      render: CustomToolbarRender,
    },
    ActionMenu: {
      tool: ActionMenu,
      render: DefaultActionMenuRender,
    },
    LinkTool: {
      tool: LinkTool,
      render: DefaultLinkToolRender,
    },
  }), []);

  // 기본값 생성 - Yoopta Editor가 기대하는 최소 구조 생성
  const getDefaultValue = useCallback(() => {
    const defaultBlockId = `paragraph-${Date.now()}`;
    const defaultElementId = `element-${Date.now()}`;
    
    return {
      [defaultBlockId]: {
        id: defaultBlockId,
        type: 'Paragraph',
        value: [
          {
            id: defaultElementId,
            type: 'paragraph',
            children: [{ text: '' }]
          }
        ],
        meta: {
          order: 0,
          depth: 0
        }
      }
    };
  }, []);

  // 데이터 정화 함수 - nodeType과 불필요한 속성 제거
  const sanitizeValue = useCallback((value) => {
    if (!value || typeof value !== 'object') return value;
    

    const sanitized = {};
    
    Object.keys(value).forEach(blockId => {
      const block = value[blockId];
      if (!block) return;
      

      // 이미지 블록인 경우 상세 정보 출력
      if (block.type === 'Image') {
        const firstElement = block.value?.[0];


        // children 내용이 있으면 텍스트 내용도 출력
        if (firstElement?.children && firstElement.children.length > 0) {
          const textContent = firstElement.children.map(child => child.text || '').join('');

          // img 태그가 있는지 확인
          if (textContent.includes('<img')) {
            const imgMatch = textContent.match(/<img[^>]+>/gi);
          }
        }
        
        // 🔍 firstElement의 모든 속성을 개별적으로 확인
        if (firstElement) {

          // props가 있다면 개별 확인
          if (firstElement.props) {
            Object.keys(firstElement.props).forEach(key => {
            });
          }
          
          // children이 있다면 개별 확인
          if (firstElement.children) {
            firstElement.children.forEach((child, index) => {
            });
          }
        }
      }
      

      const sanitizedBlock = {
        ...block,
        value: block.value?.map(element => {
          const sanitizedElement = { ...element };
          

          // props에서 nodeType 제거
          if (sanitizedElement.props) {
            const { nodeType, ...otherProps } = sanitizedElement.props;
            sanitizedElement.props = otherProps;
            
            // props가 빈 객체이면 제거 (이미지가 아닌 경우만)
            if (block.type !== 'Image' && Object.keys(sanitizedElement.props).length === 0) {
              delete sanitizedElement.props;
            }
          }
          
          // 이미지 블록의 경우 별도 처리 (props 유무와 상관없이)
          if (block.type === 'Image') {


            
            // 이미지 블록인데 요소 타입이 paragraph인 경우 수정
            if (element.type === 'paragraph') {
              sanitizedElement.type = 'image';
              
              // children에서 이미지 정보 추출 시도
              if (element.children && element.children.length > 0) {
                const textContent = element.children.map(child => child.text || '').join('');

                // img 태그가 있으면 src 추출
                const imgMatch = textContent.match(/<img[^>]+src=["']([^"']+)["'][^>]*>/i);
                if (imgMatch && imgMatch[1]) {
                  const src = imgMatch[1];
                  const altMatch = textContent.match(/<img[^>]+alt=["']([^"']+)["'][^>]*>/i);
                  const alt = altMatch ? altMatch[1] : '';
                  
                  sanitizedElement.props = {
                    ...(sanitizedElement.props || {}),
                    src: src,
                    alt: alt,
                    sizes: {
                      width: 'auto',
                      height: 'auto'
                    }
                  };
                  
                  // children을 빈 텍스트로 변경
                  sanitizedElement.children = [{ text: '' }];
                  
                } else {
                  // img 태그가 없으면 기본 이미지 구조 생성
                  sanitizedElement.props = {
                    ...(sanitizedElement.props || {}),
                    src: '',
                    alt: '',
                    sizes: {
                      width: 'auto',
                      height: 'auto'
                    }
                  };
                  sanitizedElement.children = [{ text: '' }];
                }
              } else {
                // children이 없으면 기본 이미지 구조 생성
                sanitizedElement.props = {
                  ...(sanitizedElement.props || {}),
                  src: '',
                  alt: '',
                  sizes: {
                    width: 'auto',
                    height: 'auto'
                  }
                };
                sanitizedElement.children = [{ text: '' }];
              }
            }
            
            // 이미지 블록이지만 이미 올바른 image 타입인 경우에도 props 확인
            if (element.type === 'image') {
              if (!element.props || !element.props.src) {
                sanitizedElement.props = {
                  ...(element.props || {}),
                  src: element.props?.src || '',
                  alt: element.props?.alt || '',
                  sizes: element.props?.sizes || { width: 'auto', height: 'auto' }
                };
              } else {
                // 기존 props가 완전하면 그대로 유지
                sanitizedElement.props = element.props;
              }
            }
            
            // 이미지는 props가 빈 객체여도 유지
          }
          

          return sanitizedElement;
        }) || []
      };
      
      sanitized[blockId] = sanitizedBlock;
    });
    
    return sanitized;
  }, []);

  // 단일 value 상태로 통합 - 에디터 재마운트 시 캐시 사용
  const [value, setValue] = useState(() => {
    // 캐시에서 이전 상태 복원
    const cachedValue = editorStateCache.get(editorKey);
    if (cachedValue && Object.keys(cachedValue).length > 0) {
      return cachedValue;
    }
    
    // 기본값 반환
    return getDefaultValue();
  });
  
  // value 참조 안정화 - 포커스 유지를 위해 중요함
  const valueRef = useRef(value);
  valueRef.current = value;
  
  // 에디터 준비 완료 확인 - DOM이 준비된 후에만
  useEffect(() => {
    if (editor && plugins && plugins.length > 0 && isDOMReady && mountedRef.current) {

      // 즉시 에디터 준비 상태로 설정 (지연 제거)
      setIsReady(true);
      
      // onChange와 setValue를 전역 변수로 설정
      if (onChange) {
        window.currentYooptaOnChange = onChange;
      }
      
      // 상태 업데이터를 전역 변수로 설정
      window.currentYooptaValueSetter = (newValue) => {
        if (mountedRef.current) {
          setValue(newValue);
          editorStateCache.set(editorKey, newValue);
        }
      };
    }
  }, [editor, plugins, marks, tools, onChange, editorKey, isDOMReady]);

  // 컴포넌트 언마운트 시 상태 저장 및 CSS 정리
  useEffect(() => {
    return () => {
      // 컴포넌트가 언마운트될 때 현재 상태를 캐시에 저장
      if (value) {
        editorStateCache.set(editorKey, value);
      }
      
      // 스코프된 CSS 정리 (에디터가 완전히 사라질 때만)
      // 주의: 탭 전환시에는 CSS를 유지해야 함
      if (!editorStateCache.has(editorKey)) {
        // 캐시가 없을 때만 스타일 제거 (에디터가 완전히 삭제됨)
        removeScopedCSS(scopeId);
        globalStyles.delete(editorKey);
        editorScopes.delete(editorKey);
      }
    };
  }, [value, editorKey, scopeId]);

  // 안전한 에디터 값 설정 함수
  const safeSetEditorValue = useCallback((newValue) => {
    if (!editor || !mountedRef.current) return;
    
    try {

      // 1. 에디터의 모든 selection 제거
      if (typeof editor.deselect === 'function') {
        editor.deselect();
      }
      
      // 2. Slate의 selection을 null로 설정 (Slate 내부 접근)
      if (editor.children && editor.selection) {
        try {
          // Slate 에디터의 selection을 null로 설정
          Object.assign(editor, { selection: null });
        } catch (error) {
          console.warn('[YooptaEditor] Slate selection 제거 실패 (무시됨):', error);
        }
      }
      
      // 3. DOM이 완전히 준비된 후 값 설정
      setTimeout(() => {
        if (mountedRef.current && typeof editor.setEditorValue === 'function') {
          try {
            editor.setEditorValue(newValue);
          } catch (error) {
            console.warn('[YooptaEditor] 에디터 값 설정 실패:', error);
            
            // 실패 시 에디터 재마운트
            if (window.requestYooptaRemount) {
              window.requestYooptaRemount();
            }
          }
        }
      }, 100);
      
    } catch (error) {
      console.error('[YooptaEditor] 안전한 에디터 값 설정 중 오류:', error);
      
      // 치명적 오류 시 에디터 재마운트
      if (window.requestYooptaRemount) {
        window.requestYooptaRemount();
      }
    }
  }, [editor]);

  // 초기 데이터 처리 - 조건 완화
  useEffect(() => {
    // 기본 조건만 체크 - DOM 준비 상태는 선택적으로
    if (!isReady || !editor || isInitialized || !mountedRef.current) {
      return;
    }
    

    // 짧은 대기 후 처리
    const timer = setTimeout(() => {
      if (!mountedRef.current) return;
      
      try {
        let newValue = null;
        let shouldUpdateValue = false;

        // initialData가 있고 변경된 경우에만 처리
        if (initialData && (hasInitialDataChanged.current || !isInitialized)) {

          // initialData가 Yoopta 블럭 객체인지 확인
          if (typeof initialData === 'object' && initialData !== null && !Array.isArray(initialData)) {

            
            // 🔍 이미지 블록이 있는지 원본 데이터에서 확인
            Object.keys(initialData).forEach(blockId => {
              const block = initialData[blockId];
              if (block.type === 'Image') {


                if (block.value && block.value.length > 0) {
                  block.value.forEach((element, idx) => {

                    // children의 상세 분석
                    if (element.children && element.children.length > 0) {
                      element.children.forEach((child, childIdx) => {
                      });
                      
                      const textContent = element.children.map(child => child.text || '').join('');

                      // HTML img 태그가 있는지 확인
                      if (textContent.includes('<img')) {
                        // img 태그 발견
                      }
                      
                      // 다른 형태의 이미지 정보가 있는지 확인
                      if (textContent.includes('http') || textContent.includes('data:image') || textContent.includes('.jpg') || textContent.includes('.png') || textContent.includes('.gif') || textContent.includes('.webp')) {
                        // 이미지 URL 패턴 발견
                      }
                    }
                  });
                }
                
                // 🔍 혹시 다른 필드에 이미지 정보가 있는지 확인
                const blockStr = JSON.stringify(block);
                if (blockStr.includes('http') || blockStr.includes('.jpg') || blockStr.includes('.png') || blockStr.includes('.gif') || blockStr.includes('.webp') || blockStr.includes('data:image')) {
                  // 블록 전체에서 이미지 URL 패턴 발견
                }
              }
            });
            
            newValue = sanitizeValue(initialData);
            shouldUpdateValue = true;
          }
          // initialData가 문자열인 경우 HTML 파싱 처리
          else if (typeof initialData === 'string' && initialData.trim()) {

            // 원본 HTML을 저장
            currentHtmlRef.current = initialData;
            
            // HTML에서 style 태그 분리 및 정리
            let cleanHtml = initialData;
            
            // body 태그가 있으면 내용만 추출
            if (cleanHtml.includes('<body')) {
              const bodyMatch = cleanHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
              if (bodyMatch) {
                cleanHtml = bodyMatch[1].trim();
              }
            }
            
            // yoopta-clipboard 관련 속성들 정리
            cleanHtml = cleanHtml.replace(/data-editor-id="[^"]*"/g, '');
            cleanHtml = cleanHtml.replace(/data-meta-[^=]*="[^"]*"/g, '');
            cleanHtml = cleanHtml.replace(/\s+/g, ' ').trim();
            
            // style 태그 분리 및 저장
            if (initialData.includes('<style>')) {
              const styleMatches = initialData.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
              if (styleMatches) {
                const allStyles = styleMatches.map(match => {
                  const styleContent = match.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
                  return styleContent ? styleContent[1] : '';
                }).join('\n');
                
                // CSS 정제 (보안)
                const sanitizedCSS = sanitizeCSS(allStyles);
                
                // 전역 스타일 저장소에 원본 CSS 저장 (스코프 없이)
                globalStyles.set(editorKey, sanitizedCSS);

                // 에디터 표시용으로만 스코프가 적용된 CSS를 head에 추가
                applyScopedCSS(sanitizedCSS, scopeId, `custom-style-${editorKey}`, {
                  preserveRootVars: true,  // :root의 CSS 변수 보존
                  scopeKeyframes: true     // keyframes 이름에 스코프 추가
                });
                
                // style 태그 제거
                cleanHtml = initialData.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').trim();
              }
            }
            
            // HTML을 Yoopta 형식으로 파싱 시도
            try {
              const parsedValue = html.deserialize(editor, cleanHtml);
              if (parsedValue && Object.keys(parsedValue).length > 0) {
                newValue = sanitizeValue(parsedValue);
                shouldUpdateValue = true;
              } else {
                // HTML 파싱 실패 시 텍스트로 처리
                const blockId = `paragraph-${Date.now()}`;
                const elementId = `element-${Date.now()}`;
                newValue = {
                  [blockId]: {
                    id: blockId,
                    type: 'Paragraph',
                    value: [
                      {
                        id: elementId,
                        type: 'paragraph',
                        children: [{ text: cleanHtml.replace(/<[^>]*>/g, '') }]
                      }
                    ],
                    meta: {
                      order: 0,
                      depth: 0,
                    },
                  },
                };
                shouldUpdateValue = true;
              }
            } catch (parseError) {
              console.warn('[YooptaEditor] HTML 파싱 오류:', parseError);
              // 파싱 실패 시 텍스트로 처리
              const blockId = `paragraph-${Date.now()}`;
              const elementId = `element-${Date.now()}`;
              newValue = {
                [blockId]: {
                  id: blockId,
                  type: 'Paragraph',
                  value: [
                    {
                      id: elementId,
                      type: 'paragraph',
                      children: [{ text: cleanHtml.replace(/<[^>]*>/g, '') }]
                    }
                  ],
                  meta: {
                    order: 0,
                    depth: 0,
                  },
                },
              };
              shouldUpdateValue = true;
            }
          }
        }
        // initialData가 없지만 캐시에서 복원된 값이 있는 경우
        else if (!initialData && value && Object.keys(value).length > 0) {
          newValue = value;
          shouldUpdateValue = true;
        }

        // 새로운 값이 있으면 설정
        if (newValue && shouldUpdateValue && mountedRef.current) {

          // 현재 값과 다른 경우에만 업데이트
          if (JSON.stringify(newValue) !== JSON.stringify(value)) {
            setValue(newValue);
            editorStateCache.set(editorKey, newValue);
          }
          
          // 안전한 에디터 값 설정 사용
          safeSetEditorValue(newValue);
        }

        // 초기화 완료 표시
        if (mountedRef.current) {
          setIsInitialized(true);
          hasInitialDataChanged.current = false;
        }
        
      } catch (error) {
        console.error('[YooptaEditor] 초기 데이터 처리 실패:', error);
        if (mountedRef.current) {
          setIsInitialized(true);
        }
      }
    }, 50); // 대기 시간을 더 단축
    
    return () => clearTimeout(timer);
  }, [editor, isReady, isInitialized, sanitizeValue, editorKey, initialData, value, safeSetEditorValue]);

  // 에디터 변경 핸들러 - 안정적인 상태 관리
  const handleChange = useCallback((newValue) => {
    if (!mountedRef.current) return;
    
    // 🔍 이미지 블록 변경사항 추적
    Object.keys(newValue || {}).forEach(blockId => {
      const block = newValue[blockId];
      if (block.type === 'Image') {
        if (block.value && block.value.length > 0) {
          block.value.forEach((element, idx) => {
          });
        }
      }
    });
    
    // 값이 실제로 변경되었는지 확인
    if (JSON.stringify(newValue) === JSON.stringify(valueRef.current)) {
      return; // 동일한 값이면 업데이트하지 않음
    }
    
    // 데이터 정화 후 업데이트
    const sanitizedValue = sanitizeValue(newValue || {});
    
    setValue(sanitizedValue);
    editorStateCache.set(editorKey, sanitizedValue);
    
    if (onChange && mountedRef.current) {
      // onChange를 전역 변수로 설정 (HTML/CSS 모달에서 사용)
      window.currentYooptaOnChange = onChange;
      
      // @yoopta/exports의 html 함수를 사용하여 HTML 변환
      let htmlContent = '';
      try {
        htmlContent = html.serialize(editor, sanitizedValue);
        
        // 저장된 스타일이 있으면 추가 (원본 CSS로 전달)
        const savedStyles = globalStyles.get(editorKey);
        if (savedStyles) {
          // HTML에 이미 style 태그가 없으면 추가
          if (!htmlContent.includes('<style>')) {
            htmlContent = `<style>\n${savedStyles}\n</style>\n\n${htmlContent}`;
          }
        }
      } catch (error) {
        console.warn('HTML 변환 중 오류:', error);
        // fallback: 기본적인 텍스트 변환
        const blocks = Object.values(sanitizedValue || {});
        htmlContent = blocks.map(block => {
          const text = block.value?.[0]?.children?.[0]?.text || '';
          switch (block.type) {
            case 'HeadingOne': return `<h1>${text}</h1>`;
            case 'HeadingTwo': return `<h2>${text}</h2>`;
            case 'HeadingThree': return `<h3>${text}</h3>`;
            case 'Blockquote': return `<blockquote>${text}</blockquote>`;
            case 'BulletedList': return `<ul><li>${text}</li></ul>`;
            case 'NumberedList': return `<ol><li>${text}</li></ol>`;
            case 'TodoList': return `<ul><li><input type="checkbox"> ${text}</li></ul>`;
            case 'Table': return `<table>${text}</table>`;
            case 'Code': return `<pre><code>${text}</code></pre>`;
            case 'Callout': return `<div class="callout">${text}</div>`;
            case 'Divider': return `<hr />`;
            default: return `<p>${text}</p>`;
          }
        }).join('');
      }
      // onChange에 HTML과 블럭 데이터 모두 전달
      onChange(htmlContent, sanitizedValue);
      
      // 현재 HTML 콘텐츠 저장
      currentHtmlRef.current = htmlContent;
    }
  }, [sanitizeValue, editor, editorKey, onChange]);

  // 현재 HTML 콘텐츠를 가져오는 함수
  const getCurrentHtmlContent = useCallback(() => {
    return currentHtmlRef.current;
  }, []);
  
  // 복구 중이거나 로딩 상태 체크
  if (isRecovering || !isReady || !editor) {
    return (
      <div className="yoopta-editor-wrapper">
        <div className="yoopta-editor-container">
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6b7280',
            fontSize: '16px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '10px', fontSize: '24px' }}>
                {isRecovering ? '🔄' : '⏳'}
              </div>
              <div>
                {isRecovering ? '에디터를 복구하는 중...' : t('에디터를 로딩 중입니다...')}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <YooptaErrorBoundary>
      <div className="yoopta-editor-wrapper" ref={editorContainerRef}>
        <div 
          className="yoopta-editor-container" 
          id={`yoopta-container-${editorKey}`}
          data-editor-scope={scopeId}
          onMouseDown={(e) => {
            // 마우스 다운 이벤트를 안전하게 처리
            try {
              // 에디터 영역이 아닌 곳을 클릭한 경우에만 처리
              if (e.target === e.currentTarget && mountedRef.current) {
                // 이벤트 버블링 방지
                e.stopPropagation();
                // 에디터 컨테이너 마우스 다운 이벤트 처리됨
              }
            } catch (error) {
              // 마우스 다운 이벤트 처리 중 오류 (무시됨)
            }
          }}
          onClick={(e) => {
            // 클릭 이벤트 처리 개선
            try {
              if (e.target === e.currentTarget && mountedRef.current) {
                // 이벤트 버블링 방지
                e.stopPropagation();
                
                // DOM이 준비되어 있고 Slate 노드가 존재할 때만 포커스 시도
                const slateNodes = document.querySelectorAll('[data-slate-node="element"]');
                if (slateNodes.length > 0) {
                  // 안전한 포커스 시도
                  setTimeout(() => {
                    try {
                      if (mountedRef.current && editor && typeof editor.focus === 'function') {
                        editor.focus({preventScroll: true});
                      }
                    } catch (focusError) {
                      // 클릭 포커스 실패 (무시됨)
                    }
                  }, 100);
                }
                
                // 에디터 컨테이너 클릭 이벤트 처리됨
              }
            } catch (error) {
              // 클릭 이벤트 처리 중 오류 (무시됨)
            }
          }}
        >
          {/* 직접 툴바 렌더링 */}
          <CustomToolbarRender 
            editor={editor} 
            isActive={false} 
            setIsActive={() => {}}
            editorKey={editorKey}
            onPureHtmlSave={onPureHtmlSave}
            getCurrentHtmlContent={getCurrentHtmlContent}
          />
          <div ref={selectionRootRef} style={{ 
            position: 'relative',
            border: '1px solid #eeeeee',
            padding: '5px'
          }}>
            <YooptaEditor
              key={`yoopta-${editorKey}-${editorMountKey}`}
              editor={editor}
              plugins={plugins}
              marks={marks}
              tools={{
                ActionMenu: {
                  tool: ActionMenu,
                  render: DefaultActionMenuRender,
                },
                LinkTool: {
                  tool: LinkTool,
                  render: DefaultLinkToolRender,
                },
              }}
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              autoFocus={false}
              readOnly={false}
              selectionBoxRoot={isDOMReady ? selectionRootRef.current : false}
              style={{ 
                width: '100%', 
                height: '100%',
                minHeight: '200px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
              }}
            />
          </div>
        </div>
      </div>
    </YooptaErrorBoundary>
  );
};

export default YooptaHtmlEditor; 